/**
 * The date filters on the case tables.
 *
 * Calendar days as the wire carries them, YYYY-MM-DD, and always the lab's day.
 * The caller passes today as the lab reads it rather than this module asking the
 * device's clock, which may be set anywhere. An empty end is open on that side.
 *
 * The presets are the archive's, All time, Today, This week and This month, which
 * were built and tested there before every table had a date filter.
 */

export const DATE_PRESETS = ['all', 'today', 'week', 'month', 'custom'] as const;
export type DatePreset = (typeof DATE_PRESETS)[number];

export interface DateRange {
  from: string;
  to: string;
}

/** How many days back each preset starts. The week and the month count today in. */
const PRESET_DAYS_BACK = { today: 0, week: 6, month: 29 } as const;

type SpanPreset = keyof typeof PRESET_DAYS_BACK;
const SPAN_PRESETS = Object.keys(PRESET_DAYS_BACK) as SpanPreset[];

/** The calendar day `days` before another. Arithmetic on the date alone, never on a clock. */
export function daysBefore(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

/** The range a preset stands for, open towards today. */
export function presetRange(preset: Exclude<DatePreset, 'custom'>, today: string): DateRange {
  if (preset === 'all') return { from: '', to: '' };
  return { from: daysBefore(today, PRESET_DAYS_BACK[preset]), to: '' };
}

/**
 * The preset a range was made from, so a filter restored from a link shows the
 * choice that made it rather than falling back to Custom.
 */
export function presetOf(range: DateRange, today: string): DatePreset {
  if (range.from === '' && range.to === '') return 'all';
  if (range.to !== '') return 'custom';
  const match = SPAN_PRESETS.find(
    (preset) => range.from === daysBefore(today, PRESET_DAYS_BACK[preset]),
  );
  return match ?? 'custom';
}

/** Whether a calendar day falls inside a range, both ends included. */
export function withinRange(day: string, range: DateRange): boolean {
  return (range.from === '' || day >= range.from) && (range.to === '' || day <= range.to);
}
